import { useState, useEffect, RefObject } from 'react'

/**
 * Intersection observer threshold values for visibility detection.
 * Multiple thresholds to catch any level of visibility.
 */
const VISIBILITY_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5]

/** Minimum intersection ratio to consider element visible */
const MIN_INTERSECTION_RATIO = 0.1

/** Show floating button when section is 200px above viewport (sooner) */
// const ROOT_MARGIN_SHOW_SOONER_PX = 200
/** Hide floating button when section is 300px from leaving viewport (sooner from end) */
// const ROOT_MARGIN_HIDE_SOONER_PX = 300

/**
 * Hook to track visibility of an element using Intersection Observer.
 * Useful for showing/hiding mobile sticky footers based on section visibility.
 *
 * @param ref - React ref to the element to observe
 * @returns Boolean indicating if element is visible
 *
 * @example
 * ```typescript
 * const sectionRef = useRef<HTMLElement>(null)
 * const isVisible = useAPDetailVisibility(sectionRef)
 * ```
 */
export const useAPDetailVisibility = (ref: RefObject<HTMLElement | null>): boolean => {
	const [isVisible, setIsVisible] = useState(false)

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				// Show when any part is visible and at least 10% is visible
				setIsVisible(
					entry.isIntersecting && entry.intersectionRatio >= MIN_INTERSECTION_RATIO
				)
			},
			{
				threshold: VISIBILITY_THRESHOLDS,
				rootMargin: `0px`
				// rootMargin: `${ROOT_MARGIN_SHOW_SOONER_PX}px 0px -${ROOT_MARGIN_HIDE_SOONER_PX}px 0px`
			}
		)

		const currentElement = ref.current

		if (currentElement) {
			observer.observe(currentElement)
		}

		return () => {
			if (currentElement) {
				observer.unobserve(currentElement)
			}
		}
	}, [ref])

	return isVisible
}
